import { useEffect } from "react";
import { create } from "zustand";

export interface BreadcrumbSegment {
  readonly label: string;
  /** Linkable ancestor; omit on the segment representing the current page. */
  readonly path?: string;
}

interface BreadcrumbState {
  readonly trail: readonly BreadcrumbSegment[];
  // Arrow-typed, not method shorthand: the hook below pulls these out of the
  // selector into a plain variable, which trips eslint's unbound-method
  // check for interface methods (they could reference `this`).
  readonly setTrail: (trail: readonly BreadcrumbSegment[]) => void;
  readonly clearTrail: () => void;
}

// Not persisted, unlike uiStore: a trail restored from localStorage on
// reload would show a stale record name until the owning screen re-mounts
// and republishes it.
export const useBreadcrumbStore = create<BreadcrumbState>()((set) => ({
  trail: [],
  setTrail: (trail) => set({ trail }),
  clearTrail: () => set({ trail: [] }),
}));

/**
 * Publishes a screen's trailing breadcrumb segments (record name, edit
 * label, ...) into the shared store the shell reads from, and clears them
 * on unmount so they don't leak onto the next screen. Pass `undefined`
 * while the segments' data hasn't loaded yet, to avoid a placeholder flash.
 *
 * `segments` is typically a fresh array literal built at render time, so
 * this keys the publishing effect off its serialized content rather than
 * its identity -- otherwise every unrelated re-render of the caller would
 * re-fire the effect and re-set the store.
 */
export function useBreadcrumbTrail(
  segments: readonly BreadcrumbSegment[] | undefined,
): void {
  const setTrail = useBreadcrumbStore((state) => state.setTrail);
  const clearTrail = useBreadcrumbStore((state) => state.clearTrail);
  const key = segments === undefined ? undefined : JSON.stringify(segments);

  useEffect(() => {
    if (key === undefined) return;
    setTrail(JSON.parse(key) as BreadcrumbSegment[]);
  }, [key, setTrail]);

  useEffect(() => clearTrail, [clearTrail]);
}
